import { useState, useEffect, useCallback, useRef } from "react";
import VideoCard from "./VideoCard";
import Shimmer from "../Helpers/Shimmer";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getVideoFeed, getVideosByTitle } from "../../api/video";
import { getContinueWatching } from "../../api/watchHistory";
import RobotGif from "../../assets/images/404 Robot.gif";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { MdHistory } from "react-icons/md";
import { FiSearch } from "react-icons/fi";

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 400;

export default function Body({ sidebarOpen }) {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [videos, setVideos] = useState([]);
  const [continueWatchingItems, setContinueWatchingItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [sort, setSort] = useState("recent");

  const debounceRef = useRef(null);
  const isSearching = activeQuery.trim().length > 0;

  useEffect(() => {
    if (isLoggedIn) {
      getContinueWatching()
        .then((items) => setContinueWatchingItems(items || []))
        .catch((err) => console.error("Error loading continue watching:", err));
    } else {
      setContinueWatchingItems([]);
    }
  }, [isLoggedIn]);

  const loadFirstPage = useCallback(async (query, sortValue) => {
    setLoading(true);
    try {
      const result = query.trim()
        ? await getVideosByTitle(query.trim(), 1, PAGE_SIZE, sortValue)
        : await getVideoFeed(1, PAGE_SIZE, sortValue);

      setVideos(result.videos || []);
      setPage(1);
      setHasMore(Boolean(result.hasMore));
    } catch (error) {
      console.error("Error fetching videos:", error);
      setVideos([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = activeQuery.trim()
        ? await getVideosByTitle(activeQuery.trim(), nextPage, PAGE_SIZE, sort)
        : await getVideoFeed(nextPage, PAGE_SIZE, sort);

      setVideos((prev) => [...prev, ...(result.videos || [])]);
      setPage(nextPage);
      setHasMore(Boolean(result.hasMore));
    } catch (error) {
      console.error("Error fetching more videos:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setActiveQuery(searchText);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchText]);

  useEffect(() => {
    loadFirstPage(activeQuery, sort);
  }, [activeQuery, sort, loadFirstPage]);

  const resetSearch = () => {
    setSearchText("");
    setActiveQuery("");
  };

  function DisplayCards() {
    if (loading) {
      return <Shimmer />;
    }
    if (videos.length === 0) {
      return (
        <div className="flex flex-col gap-5 py-16 justify-center items-center text-slate-300">
          <img
            src={RobotGif}
            className="rounded-2xl opacity-80"
            alt="Not found"
            width={240}
            height={240}
          />
          {isSearching ? (
            <div className="text-center">
              <p className="text-slate-400 text-sm">
                No videos found matching "<span className="text-slate-200 font-semibold">{activeQuery}</span>"
              </p>
              <button
                onClick={resetSearch}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-cyan-400 flex gap-2 items-center justify-center rounded-xl border border-slate-700 mt-4 text-xs font-semibold mx-auto transition"
              >
                <AiOutlineArrowLeft /> <span>Back to Home</span>
              </button>
            </div>
          ) : null}
        </div>
      );
    }
    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5 w-full">
          {videos.map((video) => (
            <Link to={"/video/" + video._id} key={video._id} className="flex-shrink-0">
              <VideoCard video={video} />
            </Link>
          ))}
        </div>
        {hasMore && (
          <div className="flex justify-center mt-10">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="px-6 py-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-semibold transition duration-300 disabled:opacity-50"
            >
              {loadingMore ? "Loading..." : "Load More Videos"}
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Search & Filter Header Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-center w-full mb-8 gap-4">
        <div className="relative flex items-center w-full sm:w-8/12 max-w-xl">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                setActiveQuery(searchText);
              }
            }}
            className="w-full pl-10 pr-24 py-2.5 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder-slate-500 text-slate-100 bg-[#111a2e] border border-slate-800 text-sm transition"
            placeholder="Search videos, creators, or topics..."
          />
          <FiSearch className="absolute left-3.5 w-4 h-4 text-slate-500" />
          <button
            className="absolute right-1.5 px-4 py-1.5 rounded-xl glow-button text-white text-xs font-bold transition"
            onClick={() => {
              if (debounceRef.current) clearTimeout(debounceRef.current);
              setActiveQuery(searchText);
            }}
          >
            Search
          </button>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <span className="text-xs font-medium text-slate-400">Sort by:</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3.5 py-2 rounded-xl bg-[#111a2e] border border-slate-800 text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 text-xs font-semibold cursor-pointer"
          >
            <option value="recent">Newest First</option>
            <option value="popular">Most Viewed</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
      </div>

      {/* Continue Watching Section */}
      {!isSearching && continueWatchingItems.length > 0 && (
        <div className="mb-10 glass-panel bg-[#111a2e]/60 p-4 sm:p-5 rounded-2xl border border-slate-800">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-base sm:text-lg font-bold text-slate-100 flex items-center gap-2">
              <MdHistory className="w-5 h-5 text-cyan-400" />
              Continue Watching
            </h2>
            <Link to="/history" className="text-xs text-cyan-400 hover:text-cyan-300 font-semibold transition">
              View All History →
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pt-2.5 pb-3 px-1 scrollbar-thin">
            {continueWatchingItems.map((item) => (
              <div key={item._id} className="w-60 sm:w-64 flex-shrink-0">
                <Link to={`/video/${item.video._id}`}>
                  <VideoCard video={item.video} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <DisplayCards />
    </div>
  );
}
